import { channel } from 'redux-saga';
import { replace } from 'connected-react-router';
import { get, pickBy, filter, uniqBy } from 'lodash';
import qs from 'qs';

import {
  all,
  put,
  select,
  takeEvery,
  takeLatest,
  takeLeading,
  take,
  delay,
} from 'redux-saga/effects';

import convertDateToServerFormat from '../../utils/convertDateToServerFormat';
import checkNullableValue from '../../utils/checkNullableValue';

import { MANAGER_MESSAGE_BUS } from '../../consts/managerExtension';

import {
  ATTACH_ORDER_TO_TICKET,
  CLOSE_TICKET,
  CLOSE_TICKET_SUCCESS,
  CREATE_TICKET_REQUEST,
  CREATE_TICKET_FINISH,
  CREATE_TICKET_SUCCESS,
  CREATE_TICKET,
  DETACH_ORDER_FROM_TICKET,
  FETCH_TICKETS,
  INPUT_SUGGESTED_ORDERS,
  SELECT_CLIENT,
  SELECT_TICKET,
  SET_CLOSE_REASONS_OPTIONS,
  SET_COUNTRIES_OPTIONS,
  SET_DEPARTURE_OPTIONS,
  SET_MAIL_GROUP_OPTIONS,
  SET_SKILL_GROUP_OPTIONS,
  SET_MANAGER_OPTIONS,
  SET_ORDER_SEGMENT_OPTIONS,
  SET_PAGE,
  SET_PARTNER_OPTIONS,
  SET_REASON_OPTIONS,
  SET_SOURCE_OPTIONS,
  SET_MENU_SOURCE_OPTIONS,
  SET_STATUSES_OPTIONS,
  SET_TAG_OPTIONS,
  SET_LABEL_OPTIONS,
  SET_TEMPLATE_OPTIONS,
  SET_TICKET_CLIENT,
  SET_TICKET_FILTERS,
  SET_TICKET_RELATIVES,
  SET_TICKET_SUGGESTED_ORDERS,
  SET_TICKET_UPDATED_CLIENT,
  SET_TICKET,
  SET_TICKETS,
  SET_TOUR_SELECTION,
  TRIGGER_NEW_TICKET,
  UPDATE_TICKET_CLIENT,
  UPDATE_TICKET_OWN_ORDERS,
  UPDATE_TICKET,
  SET_TICKETS_SORT,
  SET_PLAN_REASONS_OPTIONS,
  UPDATE_TICKET_IN_TICKET_LIST,
  WS_UPDATE_TICKET_IN_TICKET_LIST,
  WS_UPDATE_TICKETS_UNASSIGNED_QUEUE_COUNT,
  UPDATE_TICKETS_UNASSIGNED_QUEUE_COUNT,
  RESERVE_TICKET,
  SUCCESS_TICKET_RESERVATION,
  FAIL_TICKET_RESERVATION,
  SET_CURRENT_MANAGER_TO_INITIAL_FILTERS,
  CLEAR_TICKET_FILTERS,
  SET_TICKETS_TAB,
  SET_CLIENT_ATTACHMENTS,
  SET_IS_LOADING_ACTIVE_TICKET,
  SET_EXCESS_TICKETS,
  ADD_TICKET_IN_TICKET_LIST,
} from '../actions/tickets';

import { SHOW_NOTIFICATION } from '../actions/common';

import { WS_UPDATE_CLIENT_MESSAGE_RECEIVED } from '../actions/updates';

import fetchData from '../../api/fetchData';
import {
  serializeTicket,
  serializeTickets,
  serializeTicketsWithoutManagers,
  serializeTicketsToMap,
  searializeTicketFilters,
  serializeExcessTickets,
} from '../serializers/tickets';
import { serializeClient } from '../serializers/client';
import { serializeManagers, sortManagers } from '../serializers/manager';
import { serializeMailGroups } from '../serializers/mailGroup';
import { serializeSkillGroups } from '../serializers/skillGroup';
import { serializeTags } from '../serializers/tag';
import { serializeTemplates } from '../serializers/templates';
import { serializeTicketReasons } from '../serializers/ticketReasons';
import { serializeLabels } from '../serializers/labels';

import { ROUTE } from '../../consts/pages';
import { serializeDepartures } from '../serializers/departures';
import { groupAttachmentsByOrderID } from '../serializers/attachment';
import { serializeOrders } from '../serializers/orders';
import { serializeOrderSegments } from '../serializers/orderSegments';
import { serializePartnerToMap } from '../serializers/partners';
import { serializeSourceToMap } from '../serializers/sources';
import { serializeDestinations } from '../serializers/destination';
import { serializeTourSelection } from '../serializers/tourSelection';
import { serializeCloseReasons } from '../serializers/closeReason';
import { serializePlanReasons } from '../serializers/planReason';
import { serializeMenuSourceToMap } from '../serializers/menuSources';
import { safeApply, safeCall } from './errors';

import WebSocketConnector from '../../api/sockets/WebSocketConnector';
import {
  SOCKET_TICKET_CHANNEL,
  SOCKET_ACTIVE_TICKET_CHANNEL,
} from '../../consts/socketChannels';
import TicketChannelHandler from './socketHandlers/TicketChannelHandler';
import ActiveTicketChannelHandler from './socketHandlers/ActiveTicketChannelHandler';

import { FILTER_TABS } from '../../components/CRM/Tickets/TicketFilters/constants';
import { serializeChains } from '../serializers/chain';
import { SET_CHAINS } from '../actions/chains';
import { setNotificationsCount } from '../actions/general';

const DEPARTURES_API_URL = `${process.env.API_URL}/places/departure_cities`;
const ORDER_SEGMENTS_API_URL = `${process.env.API_URL}/orders/segments`;
const ATTACHMENTS_API_URL = `${process.env.API_URL}/orders/attachments_by_client`;
const PARTNERS_API_URL = `${process.env.API_URL}/partners`;
const PERSONS_API_URL = `${process.env.API_URL}/persons`;
const PLACE_AUTOCOMPLETE_API_URL = `${process.env.API_URL}/places/autocomplete`;
const PLAN_REASONS_API_URL = `${process.env.API_URL}/tickets/plan_reasons`;
const REASONS_API_URL = `${process.env.API_URL}/tickets/reasons`;
const SOURCES_API_URL = `${process.env.API_URL}/tickets/sources`;
const MENU_SOURCES_API_URL = `${process.env.API_URL}/tickets/menu_sources`;
const CLOSE_REASONS_API_URL = `${process.env.API_URL}/tickets/close_reasons`;
const TEMPLATES_API_URL = `${process.env.API_URL}/ticket_templates`;
const TAGS_API_URL = `${process.env.API_URL}/tags`;
export const TICKETS_API_URL = `${process.env.API_URL}/tickets`;
const ORDERS_API_URL = `${process.env.API_URL}/orders/search`;

function mergeTickets(prevTickets, nextTickets) {
  return uniqBy([...prevTickets, ...nextTickets], 'ticket.id');
}

export function* setCurrentManagerToInitialFilters() {
  const { currentManager } = yield select(state => state.general);
  const { managers } = yield select(state => state.tickets.options);
  const { id, isSupervisor } = currentManager;

  const isCurrentUserManager = !!managers.find(m => m.id === id);

  if (!id || isSupervisor || !isCurrentUserManager) return;

  yield put({
    payload: {
      managers: [id],
    },
    type: SET_CURRENT_MANAGER_TO_INITIAL_FILTERS,
  });
}

export function* fetchTicketsByClientId(clientId) {
  const { tickets, persons, tags } = yield safeCall(
    fetchData,
    `${TICKETS_API_URL}?client_id=${clientId}&new=true`
  );
  return serializeTickets({ tickets, persons, tags });
}

function* fetchTags() {
  const data = yield safeCall(fetchData, TAGS_API_URL);
  if (!data || !data.tags) return;

  const tags = yield serializeTags(data.tags);
  yield put({ payload: { tags }, type: SET_TAG_OPTIONS });
}

function* fetchPersonsLabels() {
  const apiUrl = `${PERSONS_API_URL}/labels`;
  const data = yield safeCall(fetchData, apiUrl);
  if (!data || !data.labels) return;

  const labels = serializeLabels(data.labels);
  yield put({ payload: { labels }, type: SET_LABEL_OPTIONS });
}

function* fetchTemplates() {
  const data = yield safeCall(fetchData, TEMPLATES_API_URL);
  if (!data || !data.ticket_templates) return;

  const templates = yield serializeTemplates(data.ticket_templates);
  yield put({ payload: { templates }, type: SET_TEMPLATE_OPTIONS });
}

function* fetchCloseReasons() {
  const data = yield safeCall(fetchData, CLOSE_REASONS_API_URL);
  if (!data || !data.close_reasons) return;

  const closeReasons = serializeCloseReasons(data.close_reasons);
  yield put({ payload: { closeReasons }, type: SET_CLOSE_REASONS_OPTIONS });
}

function* fetchSources() {
  const data = yield safeCall(fetchData, SOURCES_API_URL);
  if (!data || !data.ticket_sources) return;

  const sources = serializeSourceToMap(data.ticket_sources);
  yield put({ payload: { sources }, type: SET_SOURCE_OPTIONS });
}

function* fetchMenuSources() {
  const data = yield safeCall(fetchData, MENU_SOURCES_API_URL);
  if (!data || !data.menu_sources) return;

  const sources = serializeMenuSourceToMap(data.menu_sources);
  yield put({ payload: { sources }, type: SET_MENU_SOURCE_OPTIONS });
}

function* fetchReasons() {
  const { currentManager } = yield select(state => state.general);
  const { skillGroupIds: currentManagerSkillGroupIds } = currentManager;

  const data = yield safeCall(fetchData, REASONS_API_URL);
  if (!data || !data.ticket_reasons) return;

  const reasons = serializeTicketReasons(
    data.ticket_reasons,
    currentManagerSkillGroupIds
  );
  yield put({ payload: { reasons }, type: SET_REASON_OPTIONS });
}

function* fetchPartners() {
  const data = yield safeCall(fetchData, PARTNERS_API_URL);
  if (!data || !data.partners) return;

  const partners = serializePartnerToMap(data.partners);
  yield put({ payload: { partners }, type: SET_PARTNER_OPTIONS });
}

function* fetchDepartures() {
  const data = yield safeCall(fetchData, DEPARTURES_API_URL);
  if (!data || !data.cities) return;

  const departures = serializeDepartures(data.cities);
  yield put({ payload: { departures }, type: SET_DEPARTURE_OPTIONS });
}

function* fetchCountries() {
  const data = yield safeCall(fetchData, PLACE_AUTOCOMPLETE_API_URL);
  if (!data || !data.destinations) return;

  const countries = serializeDestinations(data.destinations);
  yield put({ payload: { countries }, type: SET_COUNTRIES_OPTIONS });
}

function* setPlanReasonOptions() {
  const data = yield safeCall(fetchData, PLAN_REASONS_API_URL);
  if (!data || !data.plan_reasons) return;

  const planReasons = serializePlanReasons(data.plan_reasons);
  yield put({ payload: { planReasons }, type: SET_PLAN_REASONS_OPTIONS });
}

function* fetchSuggestedOrders({ payload }) {
  yield delay(300);
  const { query = '' } = payload;
  const data = yield safeCall(fetchData, `${ORDERS_API_URL}?q=${query}`);
  const orders = serializeOrders(data.orders);

  yield put({ payload: { orders }, type: SET_TICKET_SUGGESTED_ORDERS });
}

function* fetchOrderSegments() {
  const data = yield safeCall(fetchData, ORDER_SEGMENTS_API_URL);
  if (!data || !data.segments) return;

  const orderSegments = serializeOrderSegments(data.segments);
  yield put({ payload: { orderSegments }, type: SET_ORDER_SEGMENT_OPTIONS });
}

function* fetchMailGroups() {
  const data = yield safeCall(
    fetchData,
    `${TICKETS_API_URL}/mail_groups?active_only=true`
  );
  if (!data || !data.mail_groups) return;

  const mailGroups = serializeMailGroups(data.mail_groups);
  yield put({ payload: { mailGroups }, type: SET_MAIL_GROUP_OPTIONS });
}

function* fetchSkillGroups() {
  const data = yield safeCall(fetchData, `${process.env.API_URL}/skill_groups`);
  if (!data || !data.skill_groups) return;

  const skillGroups = serializeSkillGroups(data.skill_groups);
  yield put({ payload: { skillGroups }, type: SET_SKILL_GROUP_OPTIONS });
}

export function* fetchManagers() {
  const { currentManager } = yield select(state => state.general);
  const { managers: existingManagers } = yield select(
    state => state.tickets.options
  );

  const data = yield safeCall(fetchData, `${PERSONS_API_URL}/managers`);
  if (!data || !data.managers || !!existingManagers?.length) return;

  const fetchedManagers = serializeManagers(data.managers);
  let managers = sortManagers(fetchedManagers);
  const index = managers.findIndex(({ id }) => id === currentManager?.id);

  if (index > -1) {
    const priorityManager = managers.splice(index, 1)[0];
    managers.unshift(priorityManager);
  }

  yield put({ payload: { managers }, type: SET_MANAGER_OPTIONS });
}

function* fetchTicketClient(id, type = 'client') {
  const apiUrl = `${PERSONS_API_URL}/${id}?type=${type}`;
  const data = yield safeCall(fetchData, apiUrl);
  const client = serializeClient(data.person);
  const ticketsMap = serializeTicketsToMap({ tickets: data.tickets });
  const orders = serializeOrders(data.orders) || [];
  const ordersWithTickets = orders.map(order => ({
    ...order,
    tickets:
      order.ticketIds && order.ticketIds.map(ticketId => ticketsMap[ticketId]),
  }));

  yield put({
    payload: {
      client,
      orders: ordersWithTickets,
    },
    type: SET_TICKET_CLIENT,
  });
}

function* fetchRelativeTickets(orderIds = []) {
  if (!orderIds.length) return;

  const queryParams = qs.stringify(
    { order_ids: orderIds },
    { arrayFormat: 'brackets' }
  );

  const { tickets } = yield safeCall(
    fetchData,
    `${TICKETS_API_URL}?${queryParams}`
  );
  const serializedTickets = serializeTickets({ tickets });

  const { activeTicket } = yield select(state => state.tickets);
  const ownOrders = activeTicket?.ownOrders.map(order => ({
    ...order,
    tickets: serializedTickets,
  }));

  yield put({
    type: UPDATE_TICKET_OWN_ORDERS,
    payload: {
      ownOrders,
    },
  });
}

function* fetchClientAttachments({ payload }) {
  const clientID = payload?.client?.id;

  if (!clientID) return;

  const apiUrl = `${ATTACHMENTS_API_URL}?client_id=${clientID}`;
  const { orders } = yield safeCall(fetchData, apiUrl);
  const attachmentsByOrderId = groupAttachmentsByOrderID(orders);

  yield put({
    payload: {
      clientID,
      attachmentsByOrderId,
    },
    type: SET_CLIENT_ATTACHMENTS,
  });
}

export function* updateClient({ payload }) {
  const { data } = payload;
  const apiUrl = `${PERSONS_API_URL}/${data.id}`;

  const { person } = yield safeCall(fetchData, apiUrl, {
    method: 'PUT',
    body: JSON.stringify({ ...data, type: 'client' }),
    headers: { 'Content-Type': 'application/json' },
  });
  if (!person) return;

  const client = serializeClient(person);

  yield put({ payload: { client }, type: SET_TICKET_UPDATED_CLIENT });
}

const generateFetchTicketsBackendParams = (data, excludeFields = []) => {
  const types = get(data, 'types', []);
  let kinds, subkinds;

  if (types.length > 0) {
    [kinds, subkinds] = types.reduce(
      (acc, { type, subtype }) => [
        [...acc[0], type],
        [...acc[1], subtype],
      ],
      [[], []]
    );
  }

  const queryFields = {
    kinds,
    subkinds,
    new: get(data, 'ticketsSort', 'DESC') === 'DESC',
    order_states: get(data, 'orderStatuses'),
    manager_ids: get(data, 'managers'),
    order_ids: get(data, 'orders'),
    mail_group_ids: get(data, 'mailGroups'),
    departure_date_from: convertDateToServerFormat(
      get(data, 'departureDate[0]')
    ),
    departure_date_to: convertDateToServerFormat(get(data, 'departureDate[1]')),
    client_id: get(data, 'client_id'),
    statuses: get(data, 'statuses'),
    tags: get(data, 'tags'),
    size: get(data, 'size'),
    page: get(data, 'page'),
    close_reasons: get(data, 'closeReasonStatuses'),
    user_id: get(data, 'userId'),
  };

  excludeFields.forEach(fieldName => delete queryFields[fieldName]);

  return pickBy(queryFields, v => v !== undefined);
};

const generateQueryFetchTickets = data => {
  const params = generateFetchTicketsBackendParams(data);

  return qs.stringify(params, { arrayFormat: 'brackets' });
};

const generateTicketBackendParams = params => {
  const planReasonId = get(params, 'planReasonId');
  const managerId = get(params, 'manager');
  const fields = {
    kind: get(params, 'type'),
    subkind: get(params, 'subtype'),
    subject: get(params, 'subject'),
    status: get(params, 'status'),
    mail_group_id: get(params, 'mailGroup'),
    tags: get(params, 'tags'),
    manager_id: managerId !== null ? managerId : undefined,
    is_reserved: get(params, 'isAssigned'),
    order_ids: get(params, 'orderIds'),
    planned_at: get(params, 'plannedAt'),
    reason_id: get(params, 'reason'),
    notification_channel: get(params, 'notificationChannel'),
    notification_message: get(params, 'notificationMessage'),
    planned_reason_id: planReasonId !== null ? planReasonId : undefined,
  };
  return pickBy(fields, v => v !== undefined);
};

function* watchSocketTicketsMessage(webSocketConnector, identifier) {
  const socketChannel = channel();
  const ticketChannelHandler = new TicketChannelHandler(socketChannel);
  webSocketConnector.subscribeSocketOnMessage({
    identifier,
    socketHandler: ticketChannelHandler,
  });

  while (true) {
    const action = yield take(socketChannel);
    yield put(action);
  }
}

function* subscribeSocketTickets() {
  const {
    filters,
    prevFilters,
    ticketsSort,
    prevTicketsSort,
    activeTicketsTab,
  } = yield select(state => state.tickets);

  const ticketFilters = { ...filters[activeTicketsTab], ticketsSort };
  const prevTicketFilters = {
    ...prevFilters[activeTicketsTab],
    ticketsSort: prevTicketsSort,
  };
  const { sessionId, currentManager } = yield select(state => state.general);

  const webSocketConnector = yield safeCall(WebSocketConnector.build);

  const identifier = {
    channel: SOCKET_TICKET_CHANNEL,
    user_id: `${currentManager.id}.${sessionId}`,
    data: {
      filters: searializeTicketFilters(ticketFilters),
      filter_params: generateFetchTicketsBackendParams(ticketFilters, [
        'size',
        'page',
      ]),
    },
  };

  const prevIdentifier = {
    ...identifier,
    data: {
      filters: searializeTicketFilters(prevTicketFilters),
      filter_params: generateFetchTicketsBackendParams(prevTicketFilters, [
        'size',
        'page',
      ]),
    },
  };

  webSocketConnector.unsubscribeChannel(prevIdentifier);
  webSocketConnector.subscribeChannel(identifier);

  yield safeCall(watchSocketTicketsMessage, webSocketConnector, identifier);
}

function* unsubscribeSocketTicketsTab() {
  const { filters, activeTicketsTab } = yield select(state => state.tickets);

  const prevActiveTab =
    activeTicketsTab === FILTER_TABS.ACTUAL
      ? FILTER_TABS.PLANNED
      : FILTER_TABS.ACTUAL;
  const ticketFilters = filters[prevActiveTab];
  const { sessionId, currentManager } = yield select(state => state.general);

  const webSocketConnector = yield safeCall(WebSocketConnector.build);

  const identifier = {
    channel: SOCKET_TICKET_CHANNEL,
    user_id: `${currentManager.id}.${sessionId}`,
    data: {
      filters: searializeTicketFilters(ticketFilters),
    },
  };

  webSocketConnector.unsubscribeChannel(identifier);
}

function* watchSocketActiveTicketMessage(webSocketConnector, identifier) {
  const socketChannel = channel();
  const activeTicketChannelHandler = new ActiveTicketChannelHandler(
    socketChannel
  );
  webSocketConnector.subscribeSocketOnMessage({
    identifier,
    socketHandler: activeTicketChannelHandler,
  });

  while (true) {
    const action = yield take(socketChannel);
    yield put(action);
  }
}

function* subscribeSocketActiveTicket({ payload: { ticket } }) {
  const { ticket: activeTicket } = ticket;
  const webSocketConnector = yield safeCall(WebSocketConnector.build);

  const identifier = {
    channel: SOCKET_ACTIVE_TICKET_CHANNEL,
    ticket_id: activeTicket.id,
  };

  webSocketConnector.subscribeChannel(identifier);

  yield safeCall(
    watchSocketActiveTicketMessage,
    webSocketConnector,
    identifier
  );
}

function* unsubscribeSocketActiveTicket() {
  const { prevActiveTicketId } = yield select(state => state.tickets);

  if (!prevActiveTicketId) {
    return;
  }

  const webSocketConnector = yield safeCall(WebSocketConnector.build);
  webSocketConnector.unsubscribeChannel({
    channel: SOCKET_ACTIVE_TICKET_CHANNEL,
    ticket_id: prevActiveTicketId,
  });
}

function* updateTicketByWS({ payload }) {
  const { ticketIds } = yield select(state => state.tickets);
  const updatedTicketId = get(payload, 'data.ticket.id');

  if (!ticketIds[updatedTicketId]) return;

  const ticket = serializeTicket(payload.data);
  const updatedTicketFields = pickBy(ticket, checkNullableValue);

  yield put({
    payload: { ticket: updatedTicketFields },
    type: UPDATE_TICKET_IN_TICKET_LIST,
  });
}

function* updateTicketCountByWS({ payload }) {
  const { filters } = yield select(state => state.tickets);
  const unassignedCountsByMailGroups = get(payload, 'data');
  const filteredMailGroups = get(filters, 'actual.mailGroups') || [];

  if (!unassignedCountsByMailGroups) return;

  const unassignedCount = filteredMailGroups.reduce(
    (prev, mailGroup) => prev + (unassignedCountsByMailGroups[mailGroup] || 0),
    0
  );

  yield put({
    payload: {
      unassignedCount,
      unassignedCountsByMailGroups,
    },
    type: UPDATE_TICKETS_UNASSIGNED_QUEUE_COUNT,
  });
}

function* fetchTickets() {
  const { sessionId, currentManager } = yield select(state => state.general);

  const {
    tickets: prevTickets,
    filters,
    ticketsSort,
    ticketListMeta,
    activeTicketsTab,
    unassignedCountsByMailGroups,
  } = yield select(state => state.tickets);

  const ticketFilters = filters[activeTicketsTab];

  const { size, page } = ticketListMeta;

  const queryParams = generateQueryFetchTickets({
    size,
    page,
    ticketsSort,
    userId: `${currentManager?.id}.${sessionId}`,
    ...ticketFilters,
  });

  const { meta, tickets, persons, tags } = yield safeCall(
    fetchData,
    `${TICKETS_API_URL}?${queryParams}`
  );

  const serializedTickets = serializeTicketsWithoutManagers({
    tickets,
    persons,
    tags,
  });

  const mergedTickets = mergeTickets(prevTickets, serializedTickets);
  const ticketIds = mergedTickets.reduce(
    (prev, ticket) => ({ ...prev, [ticket.id]: true }),
    {}
  );
  const mailGroupFilter = ticketFilters?.mailGroups || [];
  const unassigned = mailGroupFilter.reduce(
    (prev, mailGroup) => prev + +(unassignedCountsByMailGroups[mailGroup] || 0),
    0
  );

  yield put({
    payload: {
      tickets: mergedTickets,
      ticketIds,
      meta: {
        ...meta,
        unassigned,
      },
    },
    type: SET_TICKETS,
  });
  yield put({ type: SET_STATUSES_OPTIONS });
}

function* setTicketRelativesByClientId(clientId) {
  const tickets = yield safeCall(fetchTicketsByClientId, clientId);

  const { activeTicketId } = yield select(state => state.tickets);
  const filteredTickets = activeTicketId
    ? tickets.filter(ticket => get(ticket, 'ticket.id') !== activeTicketId)
    : tickets;

  yield put({
    payload: { tickets: filteredTickets },
    type: SET_TICKET_RELATIVES,
  });
}

function* setTourSelection(tourSelection) {
  yield put({ payload: { tourSelection }, type: SET_TOUR_SELECTION });
}

function* fetchTicket({ payload: { history, id, chainId } }) {
  yield put({ payload: true, type: SET_IS_LOADING_ACTIVE_TICKET });
  const data = yield safeCall(fetchData, `${TICKETS_API_URL}/${id}`);

  if (!get(data, 'ticket')) {
    yield put({ payload: false, type: SET_IS_LOADING_ACTIVE_TICKET });
    return;
  }

  const serializedTicket = serializeTicket(data);
  const { client, ticket } = serializedTicket;

  yield put({ payload: { ticket: serializedTicket }, type: SET_TICKET });

  const excessTicketsData = yield safeCall(
    fetchData,
    `${TICKETS_API_URL}/${id}/excess_tickets`
  );
  const excessTickets = serializeExcessTickets(
    get(excessTicketsData, 'excess_tickets', [])
  );
  yield put({ payload: { excessTickets }, type: SET_EXCESS_TICKETS });

  yield put({ payload: false, type: SET_IS_LOADING_ACTIVE_TICKET });

  const chainPath = chainId ? `/${chainId}` : '';
  if (history) history.push(`${ROUTE.TICKETS}/${id}${chainPath}`);

  yield safeCall(fetchTicketClient, client?.id);
  yield safeCall(fetchRelativeTickets, ticket?.orderIds);
  yield safeCall(setTicketRelativesByClientId, client?.id);

  if (data.tour_selection) {
    const serializedTourSelection = serializeTourSelection(data.tour_selection);
    yield safeCall(setTourSelection, serializedTourSelection);
  }
}

function* createTicket({ payload: { history, formData } }) {
  try {
    const { activeTicket } = yield select(state => state.tickets);
    const fields = generateTicketBackendParams(formData);

    const requestData = {
      client_id: activeTicket?.client?.id,
      ...fields,
    };

    const { ticket, persons, tags, orders, chains } = yield safeCall(
      fetchData,
      TICKETS_API_URL,
      {
        method: 'POST',
        body: JSON.stringify(requestData),
        headers: { 'Content-Type': 'application/json' },
        action: CREATE_TICKET,
      }
    );

    const serializedTicket = serializeTicket({ ticket, persons, tags, orders });
    yield put({
      type: CREATE_TICKET_SUCCESS,
      payload: { ticket: serializedTicket },
    });

    if (chains && chains.length) {
      const serializedChains = serializeChains(chains);

      yield put({ type: SET_CHAINS, payload: { chains: serializedChains } });
    }

    history.push(`${ROUTE.TICKETS}/${serializedTicket.id}`);
  } finally {
    yield put({ type: CREATE_TICKET_FINISH });
  }
}

function* updateTicket({ payload }) {
  const { activeTicket } = yield select(state => state.tickets);
  const id = get(activeTicket, 'ticket.id');
  const updatedFields = generateTicketBackendParams(payload);

  const apiUrl = `${TICKETS_API_URL}/${id}`;
  yield safeCall(fetchData, apiUrl, {
    method: 'PUT',
    body: JSON.stringify({
      id,
      ...updatedFields,
    }),
    headers: { 'Content-Type': 'application/json' },
    action: UPDATE_TICKET,
  });
}

function* closeTicket({ payload }) {
  const { activeTicket } = yield select(state => state.tickets);
  const { closeReasonId, orderIds } = payload;
  const ticketId = activeTicket.id;

  const apiUrl = `${TICKETS_API_URL}/${ticketId}/close`;
  const response = yield safeCall(fetchData, apiUrl, {
    method: 'POST',
    body: JSON.stringify({
      id: ticketId,
      close_reason_id: closeReasonId,
      order_ids: orderIds,
    }),
    headers: { 'Content-Type': 'application/json' },
    action: CLOSE_TICKET,
  });

  if (get(response, 'success') || get(response, 'meta.success')) {
    yield put({ type: CLOSE_TICKET_SUCCESS });
  }
}

function* attachOrderToTicket({ payload: { orderId } }) {
  const { activeTicket } = yield select(state => state.tickets);
  const orders = activeTicket.orders || [];
  const ownOrders = activeTicket.ownOrders || [];
  const ownOrderIds = ownOrders.map(or => or.id);

  const newOwnOrder = orders.find(order => order.id === orderId);

  if (!newOwnOrder) {
    return;
  }

  if (activeTicket.id) {
    yield safeCall(updateTicket, {
      payload: { orderIds: [...ownOrderIds, orderId] },
    });
  }

  yield put({
    type: UPDATE_TICKET_OWN_ORDERS,
    payload: {
      ownOrders: [...ownOrders, newOwnOrder],
    },
  });
}

function* selectClient({ payload: { history, clientId, orderId } }) {
  if (!clientId) {
    return;
  }

  if (history) {
    history.push(
      `${ROUTE.TICKETS}?client_id=${clientId}${
        orderId ? `&order_id=${orderId}` : ''
      }`
    );
  }

  const { activeTicket } = yield select(state => state.tickets);
  const currentClientId = get(activeTicket, 'client.id');

  if (clientId !== currentClientId) {
    yield safeCall(fetchTicketClient, clientId);
    yield safeCall(setTicketRelativesByClientId, clientId);
  }

  yield put({ type: TRIGGER_NEW_TICKET });
  if (orderId) {
    yield safeCall(attachOrderToTicket, { payload: { orderId } });
  }
}

function* detachOrderFromTicket({ payload: { orderId } }) {
  const { activeTicket } = yield select(state => state.tickets);
  const prevOwnOrders = activeTicket.ownOrders || [];

  const ownOrders = filter(prevOwnOrders, order => order.id !== orderId);
  const ownOrderIds = ownOrders.map(or => or.id);

  if (activeTicket && activeTicket.id) {
    yield safeCall(updateTicket, { payload: { orderIds: ownOrderIds } });
  }

  yield put({
    type: UPDATE_TICKET_OWN_ORDERS,
    payload: {
      ownOrders,
    },
  });
}

function* watchMessageCreateTicket() {
  const socketChannel = channel();
  window.addEventListener('message', event => {
    const { data } = event;
    if (data.action !== MANAGER_MESSAGE_BUS) return;

    const { clientId, orderId } = data.payload;

    if (clientId && orderId) {
      socketChannel.put(
        replace(`${ROUTE.TICKETS}?client_id=${clientId}&order_id=${orderId}`)
      );
      socketChannel.put({
        type: SELECT_CLIENT,
        payload: { clientId, orderId },
      });
    }
  });

  while (true) {
    const action = yield take(socketChannel);
    yield put(action);
  }
}

function* handleTicketReservation({ payload }) {
  const { activeTicketId } = yield select(state => state.tickets);

  if (!activeTicketId) {
    return;
  }
  const apiUrl = `${TICKETS_API_URL}/${activeTicketId}/${
    payload ? 'reserve' : 'cancel_reservation'
  }`;

  const res = yield safeCall(fetchData, apiUrl, {
    method: 'POST',
    body: '',
    headers: { 'Content-Type': 'application/json' },
  });

  if (res.meta.success) {
    yield put({ payload, type: SUCCESS_TICKET_RESERVATION });
  } else yield put({ type: FAIL_TICKET_RESERVATION });
}

function* updateClientByWS({ payload }) {
  try {
    const activeTicketClient = yield select(
      state => state.tickets.activeTicket?.client
    );
    if (!activeTicketClient) return;

    const unparsedData = get(payload, 'data');

    if (!unparsedData) return;
    if (activeTicketClient.id !== String(unparsedData.id)) return; // update only currently selected client

    const client = serializeClient(unparsedData);
    yield put({ payload: { client }, type: SET_TICKET_UPDATED_CLIENT });
  } catch (error) {
    yield put({ error, type: SET_TICKET_UPDATED_CLIENT });
  }
}

function* setTicketsRootSaga() {
  yield all([safeCall(subscribeSocketTickets)]);
}

function* notifyAboutNewTicket() {
  const isTabActive = yield select(state => state.general.isTabActive);
  const notificationsCount = yield select(
    state => state.general.notificationsCount
  );
  const isMasterInstance = yield select(
    state => state.general.isMasterInstance
  );

  const filters = yield select(state => state.tickets.filters);
  const isAnyFilterSelected = !!Object.keys(filters[FILTER_TABS.ACTUAL]).length;

  const newNotificationsCount = notificationsCount + 1;

  if (!isTabActive && isMasterInstance && isAnyFilterSelected) {
    yield put(setNotificationsCount(newNotificationsCount));

    yield put({
      payload: {
        title: `(${newNotificationsCount}) Новый тикет`,
        body: 'Вам поступил новый тикет',
      },
      type: SHOW_NOTIFICATION,
    });
  }
}

function* fetchOptions() {
  yield all([
    safeCall(fetchSources),
    safeCall(fetchMenuSources),
    safeCall(fetchManagers),
    safeCall(fetchTags),
    safeCall(fetchPersonsLabels),
    safeCall(fetchTemplates),
    safeCall(fetchMailGroups),
    safeCall(fetchSkillGroups),
    safeCall(fetchPartners),
    safeCall(fetchReasons),
    safeCall(fetchOrderSegments),
    safeCall(fetchDepartures),
    safeCall(fetchCountries),
    safeCall(fetchCloseReasons),
    safeCall(setPlanReasonOptions),
  ]);
}

export default function* () {
  yield all([
    safeCall(fetchOptions),
    safeCall(watchMessageCreateTicket),
    takeEvery(CLOSE_TICKET, safeApply(closeTicket)),
    takeLatest(
      [
        FETCH_TICKETS,
        SET_TICKET_FILTERS,
        SET_PAGE,
        SET_TICKETS_SORT,
        CLEAR_TICKET_FILTERS,
      ],
      safeApply(fetchTickets)
    ),
    takeLatest(SET_TICKET_CLIENT, safeApply(fetchClientAttachments)),
    takeLatest(
      SET_MANAGER_OPTIONS,
      safeApply(setCurrentManagerToInitialFilters)
    ),
    takeLatest(SET_TICKETS, safeApply(setTicketsRootSaga)),
    takeLatest(SET_TICKETS_TAB, safeApply(unsubscribeSocketTicketsTab)),
    takeLatest(SELECT_TICKET, safeApply(unsubscribeSocketActiveTicket)),
    takeLatest(SET_TICKET, safeApply(subscribeSocketActiveTicket)),
    takeLatest(UPDATE_TICKET_CLIENT, safeApply(updateClient)),
    takeLatest(UPDATE_TICKET, safeApply(updateTicket)),
    takeLatest(CREATE_TICKET_REQUEST, safeApply(unsubscribeSocketActiveTicket)),
    takeLeading(CREATE_TICKET, safeApply(createTicket)),
    takeLatest(CREATE_TICKET_SUCCESS, safeApply(subscribeSocketActiveTicket)),
    takeLatest([CREATE_TICKET_REQUEST, SELECT_CLIENT], safeApply(selectClient)),
    takeLatest(SELECT_TICKET, safeApply(fetchTicket)),
    takeLatest(ATTACH_ORDER_TO_TICKET, safeApply(attachOrderToTicket)),
    takeLatest(DETACH_ORDER_FROM_TICKET, safeApply(detachOrderFromTicket)),
    takeLatest(INPUT_SUGGESTED_ORDERS, safeApply(fetchSuggestedOrders)),
    takeLatest(WS_UPDATE_TICKET_IN_TICKET_LIST, safeApply(updateTicketByWS)),
    takeLatest(
      WS_UPDATE_TICKETS_UNASSIGNED_QUEUE_COUNT,
      safeApply(updateTicketCountByWS)
    ),
    takeLatest(WS_UPDATE_CLIENT_MESSAGE_RECEIVED, safeApply(updateClientByWS)),
    takeLatest(RESERVE_TICKET, safeApply(handleTicketReservation)),
    takeLatest(ADD_TICKET_IN_TICKET_LIST, safeApply(notifyAboutNewTicket)),
  ]);
}
